<template>
  <div>
    <public-announcement :is-fixed="true" />
    <invitation-notifications v-if="currentUser.id" />
    <edu-yearly-notification v-if="currentUser.id" />

    <div class="my-plan">
      <OnboardingV3BackArrow />
      <page-breadcrumb
        :category="$i18nPresenter.t('account')"
        :text="breadCumbText"
      />
      <div class="my-plan-content">
        <div class="flex justify-center mb-6" v-if="isPresenterPlanLoading">
          <aha-spinner />
        </div>
        <div class="mb-6" v-if="hasErrorOnLoadingPresenterPlan">
          <aha-section>
            <template #header>
              <h5>{{ $i18nPresenter.t('Current Plan') }}</h5>
            </template>
            <template #body>
              <div class="flex flex-col items-center">
                <p>
                  {{ $i18nPresenter.t
                    ("Cannot load your plan's details. Please try again by reloading this page.")
                  }}
                </p>
                <ui-messages type="problemOccurs" />
              </div>
            </template>
          </aha-section>
        </div>
        <FreePlanSection v-if="$store.state.plan.type == 'free'" :data="data" />
        <OneTimePlanSection
          v-else-if="$store.state.plan.type != 'free' && data.oneTime"
          :data="data"
          @getPlanColor="getPlanColor"
          @removeCardInfo="removeCardInfo"
          @showPopupUpdateCardInfo="showPopupUpdateCardInfo"
        />
        <TableCurrentPlanWithSubscription
          v-else
          :data="data"
          @getPlanColor="getPlanColor"
          :isDisplayingNextInvoicePromotion="isDisplayingNextInvoicePromotion"
          :autoRenewOn="autoRenewOn"
          :isTurningOnAutoRenew="isTurningOnAutoRenew"
          :isCancelSubscriptionProcessing="isCancelSubscriptionProcessing"
          :isCancelDataLoaded="isCancelDataLoaded"
          :isBulkLicenseSubscription="isBulkLicenseSubscription"
          @handleTurnOnAutoRenew="handleTurnOnAutoRenew"
          @showPopupUpdateCardInfo="showPopupUpdateCardInfo"
          @removeCardInfo="removeCardInfo"
          @showPromotionOnCancel="showPromotionOnCancel"
          @cancelSubscription="handleCancelSubscription"
        />

        <aha-section
          style="overflow: clip"
          class="w-full"
          id="upgrade-your-plan"
        >
          <template #header>
            <h5>
              {{ $i18nPresenter.t('Upgrade your plan') }}
            </h5>
          </template>
          <template #body>
            <div>
              <PlusLegacy
                v-if="plan && plan.group === 'plus' && currentUser.id &&
                  data.subscription && data.subscription.current_period_start &&
                  currentUser.stripeSource === 'singapore'"
              />
              <UserPricingv3 :permission="permission" @back-to-non-edu="textShowPlanEdu = true" />
            </div>
          </template>
        </aha-section>
        <div
          class="mb-20 text-sm text-base-80"
        >
          <p class="text-center">
            <span v-html="$i18nPresenter.t('forOtherInquiries')"></span>
          </p>
        </div>
      </div>
      <AutoRenewModal
        v-if="isConfirmCancel"
        :titleAutoRenewal="title_AutoRenewal"
        @handleModalConfirmOk="handleModalConfirmOk"
      />

      <!-- Modal reminder promotion on cancel subscriptionoffer -->
      <PromotionOnCancelReminderModal
        v-if="isPromotionOnCancelReminderPopupVisible"
        @handleClosePromotionOnCancelReminderPopup="handleClosePromotionOnCancelReminderPopup"
        @handleContinueWithCancellation="handleContinueWithCancellation"
      />
      <modal-update-card-info
        @handleCloseUpdateCardInfo="handleCloseUpdateCardInfo"
        v-if="showUpdateCardInfo"
      />

      <CancelSubscriptionProcess
        v-if="isCancelSubscriptionProcessing"
        :subscription="data && data.subscription"
        @endLoading="isCancelDataLoaded = true"
        @endProcess="handleCancelSubscription(false)"
      />
    </div>
  </div>
</template>

<script>
// @ is an alias to /src
import { mapActions, mapState } from 'vuex'
import Config from '@/config'
import EventBus from '@/eventBus'
import Util from '@/util'
import promotionCodeUtils from '@/utils/promotionCodeUtils'
import Pricing from '@/pricing'

// UI
import Messages from '@/components/UI/Messages'
import PublicAnnouncement from '@/components-v2/common/global-notification/PublicAnnouncement.vue'
import InvitationNotifications from '@/components-v2/common/global-notification/InvitationNotifications.vue'
import EduYearlyNotification from '@/components-v2/common/global-notification/EduYearlyNotification.vue'
import ModalUpdateCardInfo from '../modals/UpdateCardInfo.vue'
import PageBreadcrumb from '../common/page-breadcrumb/PageBreadcrumb.vue'
import TableCurrentPlanWithSubscription from './TableCurrentPlanWithSubscription.vue'
import OnboardingV3BackArrow from '../common/onboarding/OnboardingV3BackArrow.vue'
import CancelSubscriptionProcess from '@/components-v2/my-plan/cancel-subscription/CancelSubscriptionProcess.vue'
import OneTimePlanSection from './OneTimePlanSection.vue'
import eventCategories from '@/constant/eventCategories'
import FreePlanSection from './FreePlanSection.vue'

// v3
import UserPricingv3 from '@/components-v2/my-plan/user-pricing-v3/UserPricing.vue'

// helpers
import { isDisplayingNextInvoicePromotion } from './userPlan.helper'
import PlanAPI from '@/api/plan'
import AutoRenewModal from './AutoRenewModal.vue'
import PromotionOnCancelReminderModal from '@/components-v2/my-plan/PromotionOnCancelReminderModal.vue'
import PlusLegacy from '@/components-v2/my-plan/PlusLegacy'

export default {
  name: 'UserPlan',
  components: {
    PromotionOnCancelReminderModal,
    AutoRenewModal,
    ModalUpdateCardInfo,
    PageBreadcrumb,
    UiMessages: Messages,
    UserPricingv3,
    TableCurrentPlanWithSubscription,
    InvitationNotifications,
    EduYearlyNotification,
    PublicAnnouncement,
    OnboardingV3BackArrow,
    CancelSubscriptionProcess,
    OneTimePlanSection,
    FreePlanSection,
    PlusLegacy,
  },

  data() {
    return {
      isPresenterPlanLoading: false,
      isPromotionOnCancelReminderPopupVisible: false,
      data: {
        plan: null,
        subscription: null,
        card: null,
        address: null,
      },
      typePay: 1,
      url: '',
      isTurningOnAutoRenew: false,
      dataConfirm: {},
      autoRenewOn: false,
      isConfirmCancel: false,
      isCheckout: false,
      stripe: null,
      coupon: null,
      hasErrorOnLoadingPresenterPlan: false,
      showUpdateCardInfo: false,
      isRemoving: false,
      textShowPlanEdu: true,
      permission: {
        imeMini: false,
        oneTimeSmall: false,
        oneTimeMedium: false,
        oneTimeLarge: false,
        essentialMonthly: false,
        essentialYearly: false,
        plusMonthly: false,
        plusYearly: false,
        proMonthly: false,
        proYearly: false,
        eduSmall: false,
        eduMedium: false,
        eduLarge: false,
        eduSmallYearly: false,
        eduMediumYearly: false,
        eduLargeYearly: false,
      },
      title_AutoRenewal: this.$i18nPresenter.t('Auto-renewal is turned on'),
      isCancelSubscriptionProcessing: false,
      isCancelDataLoaded: false,
      useV3: false,
    }
  },
  computed: {
    ...mapState({
      currentUser: (state) => state.currentUser,
      plan: (state) => state.plan,
      planBenefit: (state) => state.planBenefit,
      promotionCode: (state) => state.promotionCode,
    }),
    breadCumbText() {
      return this.$i18nPresenter.t('My Plan')
    },
    abTestOpenEdu() {
      return true
    },
    isTextShowPlanEdu() {
      return this.textShowPlanEdu && this.abTestOpenEdu
    },
    promotionCodeUsed() {
      return this.data && this.data.subscription && this.data.subscription.coupon
    },
    isBulkLicenseSubscription() {
      return this.data.subscription && this.data.subscription.quantity > 1
    },
    isDisplayingNextInvoicePromotion() {
      return isDisplayingNextInvoicePromotion(this.data)
    },
    offerPercentage() {
      if (!this.currentUser.id) return null
      if (this.currentUser.id % 2 === 0) return '25%'
      return '50%'
    },
  },
  watch: {
    '$store.state.currentUser': async function () {
      if (this.currentUser.id) {
        await this.getMyPlan()
        this.getV3Plan()
        await this.checkPromotion()
        this.scrollToSection()
        this.stripe = window.Stripe(Config.singaporeStripePublishableKey)
      }
    },
  },

  async mounted() {
    document.body.classList.remove('no-scroll')

    if (this.currentUser.id) {
      await this.getMyPlan()
      this.getV3Plan()
      this.checkPromotion()
      this.stripe = window.Stripe(Config.singaporeStripePublishableKey)
    }

    this.hideAnnoucementProcessing()
    this.url = Config.presenterDomain
    EventBus.$on('handleTurnOnAutoRenew', (notShowPopupDone) => {
      this._turnOnAutoRenew(notShowPopupDone)
    })
  },
  destroyed() {
    EventBus.$off('handleTurnOnAutoRenew')
  },
  methods: {
    ...mapActions(['updateRemoveCardPurposeAction', 'setIsWelcomePopupShowingAction',
      'getPromotionCode', 'setIsOnboardingV3ShowingAction', 'setHasOnboardingV3Action',
      'setIsValidPromoCodeAction']),
    hideAnnoucementProcessing() {
      if (window.localStorage.getItem('hideNewPriceAnnoucement') == 'true') this.showAnnouncementNewPlan = false
    },
    scrollToSection() {
      if (window.location.hash && window.location.hash.length > 0) {
        const nameDom = window.location.hash.replace('#', '')
        const el = document.getElementById(nameDom)
        if (el) {
          el.scrollIntoView({ behavior: 'smooth' })
        }
      }
    },
    async checkPromotion() {
      const promotion_code = promotionCodeUtils.getPromotionCodeToLocalStore()
      if (!promotion_code) return
      await this.getPromotionCode(promotion_code)
      if (!this.promotionCode) {
        return
      }
      const used = this.promotionCodeUsed && this.promotionCodeUsed.id.trim()
      const apply = this.promotionCode
          && this.promotionCode.coupon && this.promotionCode.coupon.id.trim()
      if (used === apply) {
        this.setIsValidPromoCodeAction(false)
        return
      }
      this.setIsValidPromoCodeAction(true)
      this.hiddenOnBoardingWhenHavePromo()
    },
    hiddenOnBoardingWhenHavePromo() {
      this.setHasOnboardingV3Action(false)
      setTimeout(() => {
        document.body.classList.remove('no-scroll')
      }, 1000)
    },
    showEduPlan() {
      this.textShowPlanEdu = false
      EventBus.$emit('clickShowEduPlan')
    },
    async getMyPlan() {
      this.checkToScrollToBulkPricing()

      if (this.currentUser.plan === 'free') {
        const res = await PlanAPI.getMyPlan()
        this.data.card = res.body.card
        this.data.address = res.body.address
        this.data.plan = this.$store.state.plan
        return
      }

      this.isPresenterPlanLoading = true
      this.hasErrorOnLoadingPresenterPlan = false

      try {
        const res = await PlanAPI.getMyPlan()
        this.data = res.body

        if (this.data.subscription) {
          this.data.subscription.plan = Pricing.getPlanByType(res.body.subscription.type)
        }
      } catch (error) {
        this.hasErrorOnLoadingPresenterPlan = true
        Util.mixpanelTracking('User Plan', 'Get user plan', 'Get user plan failed')
        throw error
      } finally {
        this.isPresenterPlanLoading = false
      }
      Pricing._checkPermissionUpgrade(this.$store.state.plan.type, this.permission, this.data)
    },

    getPlanColor(plan) {
      if (!plan) return ''
      const planName = plan.name
      const lowercasePlan = planName.toLowerCase()
      if (lowercasePlan.includes('medium') || lowercasePlan.includes('plus')) {
        return 'text-green-50'
      }
      if (lowercasePlan.includes('small') || lowercasePlan.includes('essential')) {
        return 'text-yellow-50'
      }
      if (lowercasePlan.includes('pro') || lowercasePlan.includes('large')) {
        return 'text-purple-50'
      }
      if (lowercasePlan.includes('white label')) {
        return 'text-secondary-50'
      }
      return 'text-primary-90'
    },

    handleTurnOnAutoRenew() {
      if (!this.data.card) {
        this.showUpdateCardInfo = true
      } else {
        this._turnOnAutoRenew()
      }
    },

    _turnOnAutoRenew(notShowPopupDone) {
      // API turn on auto renew
      if (this.isTurningOnAutoRenew) return
      this.isTurningOnAutoRenew = true
      this.$http
        .post(Config.api.changeSubscription, {
          stop: false,
        })
        .then(() => {
          this.isTurningOnAutoRenew = false
          this.data.subscription.cancel_at_period_end = false
          Util.mixpanelTracking(
            'User Plan',
            'User Turn on Auto-renewal',
            'User Turn on Auto-renewal Success',
          )
          if (notShowPopupDone) return
          this.dataConfirm = {
            title: 'Auto-renewal is turned on',
            description: 'Thank you for using AhaSlides!',
            noCancelBtn: true,
          }
          this.isConfirmCancel = true
        })
    },
    handleModalConfirmOk() {
      this.isConfirmCancel = false
    },

    handleClosePromotionOnCancelReminderPopup() {
      Util.mixpanelTracking(
        eventCategories.MY_PLAN,
        'Auto-Offer on cancellation > Offer reminder > I"ll stay',
        `Offer: ${this.offerPercentage}`,
      )
      this.isPromotionOnCancelReminderPopupVisible = false
    },

    handleContinueWithCancellation() {
      this.isPromotionOnCancelReminderPopupVisible = false
      this.updateRemoveCardPurposeAction(true)
      this.handleCancelSubscription(true)
    },

    removeCardInfo() {
      if (this.isDisplayingNextInvoicePromotion) {
        this.isPromotionOnCancelReminderPopupVisible = true
        return
      }

      this.updateRemoveCardPurposeAction(true)
      this.handleCancelSubscription(true)
    },
    showPromotionOnCancel() {
      this.isPromotionOnCancelReminderPopupVisible = true
    },

    showPopupUpdateCardInfo() {
      Util.mixpanelTracking(
        'User Plan',
        'User update card info',
        'User click on update card info',
      )
      this.showUpdateCardInfo = true
    },

    handleCloseUpdateCardInfo(card) {
      this.isTurningOnAutoRenew = false
      this.showUpdateCardInfo = false
      if (card) {
        this.data.card = card
      }
    },
    handleCancelSubscription(status = true) {
      this.isCancelSubscriptionProcessing = status
    },
    checkToScrollToBulkPricing() {
      if (this.$route.hash != '#bulk-pricing') return
      setTimeout(() => {
        const bulkPricingSection = document.getElementById('bulk-pricing')
        if (!bulkPricingSection) return
        window.scrollTo({
          top: bulkPricingSection.offsetTop,
          behavior: 'smooth',
        })
      }, 500)
    },
    getV3Plan() {
      if (this.$store.state.currentUser.country) {
        this.useV3 = this.isAbTest(this.$store.state.currentUser.country)
      }
    },
    isAbTest(country) {
      const NEW_FREE_PLAN_TEST_GROUP_CONTRIES = [
        'vietnam',
        'singapore',
        'netherlands',
        'taiwan',
        'germany',
        'italy',
        'philippines',
        'spain',
        'malaysia',
        'mexico',
        'uzbekistan',
        // add more countries for test group here
      ]
      if (!country) return false
      return NEW_FREE_PLAN_TEST_GROUP_CONTRIES.includes(country.toLowerCase())
    },
  },
}
</script>

<style lang="scss" >
.my-plan {
  @apply p-4 md:px-10 md:py-6 relative;
}

.my-plan-content {
  @apply w-full flex flex-col justify-center md:mx-auto;
  max-width: 1280px;
}

.announcement {
  @apply flex flex-row items-center py-2.5 px-4 my-6 gap-2 rounded-lg relative;
  background: #D3E6FD;
  span {
    @apply mr-4;
  }
  b {
    font-weight: 700;
    color: black;
    text-decoration-line: underline;
  }
  i {
    color: rgba(40, 46, 62, 1);
  }
}
.table-plan {
  @apply w-full;
}

.table-plan-property {
  @apply font-bold py-2 w-1/2 md:w-1/3 align-top;
}

.table-plan-value {
  @apply py-2 w-1/2 md:w-2/3 align-top;
  word-break: break-word;
}

@media (min-width: 1281px) {
  .title-open-edu-mobile{
    display: none;
  }
}

.title-open-edu-mobile{
  span{
    b{
      font-style: normal;
      font-weight: 700;
      font-size: 16px;
      line-height: 22px;
    }
  }

  svg{
    display: inline-block;
    vertical-align: baseline;
  }

  a {
    font-weight: 700;
    font-size: 14px;
  }
  font-weight: 400;
  font-size: 14px;
  line-height: 18px;
  margin-bottom: 25px;
}

.enterprise-cta {
  .group-icon {
    min-width: 48px;
  }
}

.cta-button {
  border-radius: 20px;
  border: 0;
  min-width: 160px;
  color: #fff;
  cursor: pointer;
  letter-spacing: 1px;
  text-transform: uppercase;
  background-color: #6A1EBB;
  transition: 0.15s linear;
  height: 34px;
  padding: 0 20px;
  font-size: 0.8rem;
  line-height: 0;
  &:hover {
    background-color: #1178ee;
  }
}

.cta-button:focus {
  outline: none;
}
.global-notification-bar {
  @apply flex flex-col px-3 py-2  sm:flex-row items-start sm:items-center;
  @apply min-h-11 bg-primary-75 text-base-100;
  @apply relative;
  border-radius: 8px;

  font-size: 14px;
  .text-email{
    font-weight: 700;
    text-decoration-line: underline;
  }

}

</style>
